/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React version of DocIcon
 * Based on the original JSX version in /lego/page/browser/doc-icon/DocIcon.jsx
 *
 * Key differences from JSX version:
 * - Uses useNavigate hook from React Router v7
 * - Uses TypeScript interfaces for type safety
 * - Modern React patterns with proper typing
 * - Simplified service access (services may need to be implemented)
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { AclLevel, FsiType } from '../../constants/fsi'
import FsiExtImg from './FsiExtImg.js'
import FsiPropertiesModal from '../modals/FsiPropertiesModal'
import { useModal } from '~/contexts/ModalContext'
import { buildUrlWithCSelections } from '~/utils/navigation'
import { validateFileFactory } from '~/services/factory/validateFileFactory'

interface FsiAcl {
  level: string
}

interface FileSystemItem {
  id: string | number
  name?: string
  type?: string
  valid?: boolean
  systemFolderType?: string
  acl?: FsiAcl | string
  // Additional properties that might come from Document interface
  lastModified?: string | number
  [key: string]: any // Allow additional properties
}

interface DocIconProps {
  fsi: FileSystemItem
  inWorkflow?: boolean
  onClickDownload?: () => void
  onClickProperties?: () => void
}

const DocIcon: React.FC<DocIconProps> = ({ fsi, inWorkflow = false, onClickDownload, onClickProperties }) => {
  const navigate = useNavigate()
  const [acl, setAcl] = useState<string | null>(null)
  const { showModal } = useModal()
  const validateFile = validateFileFactory()

  const downloadFile = () => {
    // This would typically call a download service
    // downloadService.downloadOriginal(fsi.id);
    validateFile.downloadOriginal(Number(fsi.id))
  }

  const openPropertiesModal = () => {
    // This would typically open a properties modal
    showModal(
      <FsiPropertiesModal
        fsiId={fsi.id}
        onClose={function (): void {
          throw new Error('Function not implemented.')
        }}
      />
    )
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Allow right-click / ctrl+click / cmd+click to use the native href
    if (e.metaKey || e.ctrlKey || e.button !== 0) return
    e.preventDefault()

    if (!onClickDownload && !onClickProperties) {
      const fileType = fsi?.type || FsiType.DOCUMENT
      if (fileType === FsiType.FOLDER) {
        navigate(`/browser/${fsi.id}?type=${fsi.systemFolderType || ''}&page=1`)
      } else {
        navigate(`/show-file/${fsi.id}`)
      }
    } else if (onClickDownload) {
      downloadFile()
    } else if (onClickProperties) {
      openPropertiesModal()
    }
  }

  useEffect(() => {
    if (fsi?.acl) {
      // Handle both object with level property and direct string value
      const aclLevel = typeof fsi.acl === 'object' && fsi.acl.level ? fsi.acl.level : fsi.acl
      setAcl(aclLevel as string)
    } else {
      setAcl(null)
    }
  }, [fsi])

  // Determine if the icon should be clickable
  const fileType = fsi?.type || FsiType.DOCUMENT
  const isClickable = acl !== AclLevel.BROWSE && fileType !== FsiType.PLACEHOLDER && fsi.valid !== false // Default to true if valid is undefined

  // Build a real href so right-click → Open in New Tab works
  // buildUrlWithCSelections handles both MFE (with c_selections) and DSO (without) modes
  const href = useMemo(() => {
    if (onClickDownload || onClickProperties) return '#'
    if (fileType === FsiType.FOLDER) {
      return buildUrlWithCSelections(`/browser/${fsi.id}?type=${fsi.systemFolderType || ''}&page=1`)
    }
    return buildUrlWithCSelections(`/show-file/${fsi.id}`)
  }, [fsi.id, fsi.systemFolderType, fileType, onClickDownload, onClickProperties])

  return (
    <>
      {isClickable ? (
        <a
          href={href}
          onClick={handleClick}
          className="doc-icon-link"
          title={`Open ${fileType === FsiType.FOLDER ? 'folder' : 'file'}`}
        >
          <FsiExtImg fsi={fsi} inWorkflow={inWorkflow} />
        </a>
      ) : (
        <FsiExtImg fsi={fsi} inWorkflow={inWorkflow} />
      )}
    </>
  )
}

export default DocIcon
